/**
 * Every failure the API can report, in the user's vocabulary — plan §04.4.
 *
 * `Record<ErrorCode, …>` is the point of the file. It is exhaustive over the
 * union imported from `@rd/shared`, so adding a code to the API without adding
 * copy here is a **compile error**, not a screen that says
 * `UPSTREAM_NOT_ENTITLED`. Criterion 13 depends on that being a type error
 * rather than a lint rule someone can suppress.
 *
 * Three rules the copy itself obeys:
 *
 *   1. **No vendor vocabulary.** No operator name, no upstream credential
 *      field name, no upstream path — the exact list lives in
 *      `test/errorCopy.spec.ts`, which is not bundled. Naming them here would
 *      put them in the sourcemap, which ships. The user bought a panel from us;
 *      who we buy from is not their concern, and under `SUPPRESS` it is
 *      deliberately hidden (§7.1). Copy says "the network".
 *   2. **No internal vocabulary.** "Refresh token reused" is our word for a
 *      security event. The user's word is "your session ended".
 *   3. **No `requestId` in prose.** It belongs behind a "details" affordance
 *      for someone raising a ticket, not in the middle of a sentence.
 */

import { ERROR_CODES, type ErrorCode } from '@rd/shared';

import { ApiClientError, NetworkError } from './apiClient.js';

export interface ErrorCopy {
  title: string;
  body: string;
  /** Label for the primary recovery control, when one makes sense. */
  action?: string;
}

export const ERROR_COPY: Record<ErrorCode, ErrorCopy> = {
  /* --- Authentication ------------------------------------------------ */
  AUTH_REQUIRED: {
    title: 'Please sign in',
    body: 'You need to sign in to see this.',
    action: 'Go to sign in',
  },
  AUTH_TOKEN_EXPIRED: {
    // Normally invisible — the client refreshes and replays. Copy exists for
    // the case where the refresh itself is what failed.
    title: 'Your session needs renewing',
    body: 'Signing you back in…',
  },
  AUTH_INVALID_CREDENTIALS: {
    // §04.4: identical for an unknown address and a wrong password. Neither
    // field is named as the culprit, because the server cannot tell you which
    // and pretending otherwise would undo that.
    title: 'Incorrect email address or password.',
    body: 'Check both and try again.',
  },
  AUTH_ACCOUNT_LOCKED: {
    title: 'Too many sign-in attempts',
    body: 'Try again in about 15 minutes, or contact your administrator.',
  },
  AUTH_ACCOUNT_DISABLED: {
    // Covers a suspended tenant AND a disabled user. Same words for both: which
    // one it is would tell an outsider whether the account exists.
    title: 'This account is not active',
    body: 'Contact your administrator.',
  },
  AUTH_PASSWORD_EXPIRED: {
    title: 'Your password needs changing',
    body: 'Choose a new password to carry on.',
    action: 'Change password',
  },
  AUTH_REFRESH_INVALID: {
    title: 'Your session ended',
    body: 'Please sign in again.',
    action: 'Sign in',
  },
  AUTH_REFRESH_REUSED: {
    // Never "token reused" — that is our vocabulary for a security event, and
    // to the person reading it, it sounds like an accusation.
    title: 'Your session ended',
    body: 'Please sign in again.',
    action: 'Sign in',
  },
  AUTH_TOTP_INVALID: {
    // Never "TOTP" — that is our word for the mechanism. The user's word is
    // the code their authenticator app shows them.
    title: 'That code is not correct',
    body: 'Check your authenticator app and try again.',
  },
  AUTH_TOTP_CHALLENGE_EXPIRED: {
    // The password step succeeded but the code step took too long. Starting
    // over is the only recovery, so say that rather than explaining the
    // two-step handshake.
    title: 'That sign-in attempt expired',
    body: 'Please sign in again.',
    action: 'Sign in',
  },

  /* --- Authorization -------------------------------------------------- */
  FORBIDDEN_PERMISSION: {
    title: 'You do not have access to this',
    body: 'Your administrator can grant it if you need it.',
  },
  FORBIDDEN_FEATURE: {
    title: 'Not available on this account',
    body: 'This feature is not enabled here. Contact your administrator to ask about it.',
  },
  FORBIDDEN_TENANT: {
    title: 'Choose an account first',
    body: 'This action needs an account selected.',
  },
  FORBIDDEN_ORIGIN: {
    // Almost nobody should ever see this: it means the page is served from
    // somewhere the API is not configured to accept. That is a deployment
    // problem, so the copy points at the person who can fix it — and names no
    // origin, because telling a refused caller which origins ARE allowed turns
    // a refusal into a configuration disclosure.
    title: 'This page cannot reach the panel',
    body: 'It is being served from an address the panel does not accept. Contact your administrator.',
  },

  /* --- Request -------------------------------------------------------- */
  VALIDATION_FAILED: {
    title: 'Check the highlighted fields',
    body: 'Something in the form needs correcting.',
  },
  NOT_FOUND: {
    title: 'Not found',
    body: 'This may have been removed, or you may not have access to it.',
  },
  CONFLICT: {
    title: 'That is already in use',
    body: 'Choose a different value and try again.',
  },
  IDEMPOTENCY_MISMATCH: {
    title: 'This request was already sent',
    body: 'It was sent earlier with different details. Reload the page and try once more.',
    action: 'Reload',
  },
  PAYLOAD_TOO_LARGE: {
    title: 'That file is too large',
    body: 'Choose a smaller file and try again.',
  },
  UNSUPPORTED_MEDIA_TYPE: {
    title: 'That file type is not supported',
    body: 'Choose a PNG, JPEG, WebP, ICO or SVG image.',
  },
  RATE_LIMITED: {
    title: 'Too many requests',
    body: 'Wait a moment and try again.',
    action: 'Try again',
  },

  /* --- Upstream ------------------------------------------------------- *
   * "The network" throughout, never the operator's name. In SUPPRESS mode the
   * tenant is not told who the network is, and copy is not an exception.      */
  UPSTREAM_REJECTED: {
    title: 'The network refused this',
    body: 'Nothing was changed. Check the details and try again.',
  },
  UPSTREAM_NOT_ENTITLED: {
    title: 'Not available on this account',
    body: 'This account is not set up for that. Contact your administrator.',
  },
  UPSTREAM_AUTH_FAILED: {
    title: 'This account is not connected',
    body: 'We could not authenticate with the network. Your administrator needs to check the account settings.',
  },
  UPSTREAM_RATE_LIMITED: {
    title: 'The network is busy',
    body: 'Too many requests were sent. Wait a moment and try again.',
    action: 'Try again',
  },
  UPSTREAM_UNAVAILABLE: {
    title: 'The network is not responding',
    body: 'Nothing was changed. This usually clears on its own.',
    action: 'Try again',
  },
  UPSTREAM_MALFORMED: {
    title: 'We could not read the reply',
    body: 'The network answered in a way we did not expect. We have logged it.',
  },
  VENDOR_NOT_CONFIGURED: {
    title: 'This account is not connected yet',
    body: 'Your administrator needs to finish setting it up before this will work.',
  },
  JOB_NOT_READY: {
    title: 'Still working',
    body: 'This is not finished yet. It will update on its own.',
  },

  /* --- Location ------------------------------------------------------- */
  LOCATION_NOT_WHITELISTED: {
    title: 'Location is not enabled for this SIM',
    body: 'Add it to the location whitelist first.',
  },
  LOCATION_DEVICE_OFF: {
    title: 'The device is switched off',
    body: 'We cannot locate a SIM while its device is powered down.',
  },
  LOCATION_DEVICE_UNREACHABLE: {
    title: 'The device could not be reached',
    body: 'It may be out of coverage. Try again later.',
    action: 'Try again',
  },
  LOCATION_ROAMING_BLOCKED: {
    title: 'The device is roaming',
    body: 'Location is not available for this SIM while it is on another network.',
  },

  /* --- Everything else ------------------------------------------------ */
  INTERNAL_ERROR: {
    title: 'Something went wrong at our end',
    body: 'This is not your fault. Try again, and tell us if it keeps happening.',
    action: 'Try again',
  },
};

const NETWORK_COPY: ErrorCopy = {
  title: 'No connection to the panel',
  body: 'Check your internet connection. Nothing you did was lost.',
  action: 'Try again',
};

const UNKNOWN_COPY: ErrorCopy = ERROR_COPY.INTERNAL_ERROR;

/**
 * Copy for anything thrown anywhere in the app.
 *
 * `RATE_LIMITED` is the one code whose body is dynamic, because "wait a moment"
 * is useless next to a `Retry-After` of 900.
 */
export function copyFor(err: unknown): ErrorCopy {
  if (err instanceof NetworkError) return NETWORK_COPY;

  if (err instanceof ApiClientError) {
    const base = ERROR_COPY[err.code] ?? UNKNOWN_COPY;

    if (err.code === 'RATE_LIMITED' && err.retryAfterSeconds !== null) {
      return { ...base, body: `Try again in ${formatSeconds(err.retryAfterSeconds)}.` };
    }

    // A validation failure with a message the server bothered to write is more
    // specific than ours; a generic one is not.
    if (err.code === 'VALIDATION_FAILED' && err.details.length === 0 && err.message) {
      return { ...base, body: err.message };
    }

    return base;
  }

  return UNKNOWN_COPY;
}

export function formatSeconds(seconds: number): string {
  if (seconds < 60) return `${Math.max(1, Math.round(seconds))} seconds`;
  const minutes = Math.round(seconds / 60);
  return minutes === 1 ? 'a minute' : `${minutes} minutes`;
}

/**
 * Guard for the copy test: the catalog in `@rd/shared` and this map must have
 * the same keys. Exported so the assertion reads as one comparison rather than
 * a loop the test has to get right.
 */
export function copyCoverage(): { missing: string[]; extra: string[] } {
  const codes = Object.keys(ERROR_CODES);
  const copy = Object.keys(ERROR_COPY);
  return {
    missing: codes.filter((c) => !copy.includes(c)),
    extra: copy.filter((c) => !codes.includes(c)),
  };
}
